import {
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query';

import { env } from '@/app/config/env';
import { logout } from '@/features/auth/store/auth-slice';
import { clearBackofficeSession } from '@/features/backoffice2020/store/backoffice-auth-slice';
import type { RootState } from '@/app/store/root-reducer';

const MAX_RETRIES = 2;

function isFormDataRequest(arg: unknown): boolean {
  if (!arg || typeof arg !== 'object') {
    return false;
  }

  const request = arg as { body?: unknown; formData?: unknown };
  return (
    request.body instanceof FormData || request.formData instanceof FormData
  );
}

function getRequestUrl(args: string | FetchArgs): string {
  if (typeof args === 'string') {
    return args;
  }
  return args.url;
}

function isBackofficeLoginRequest(url: string): boolean {
  return url.includes('/auth/backoffice/login');
}

function isBackofficeApiRequest(url: string): boolean {
  return (
    url.includes('/auth/backoffice/') ||
    url.includes('/backoffice/') ||
    url.includes('/getbranchcodesassociatedwithuser/') ||
    url.includes('/communicationservice/smscontroller/') ||
    url.includes('/communicationservice/api/smscontroller/') ||
    url.includes('/communicationservice/pro20/mail/config') ||
    url.includes('/app-version')
  );
}

/** Used by both panel bookings and backoffice Message Monitor. */
function isSharedMessageDeliveryRequest(url: string): boolean {
  return url.includes('/message-delivery-events');
}

/** Stripe Connect onboarding is driven from the backoffice but not under /backoffice/. */
function isStripeConnectRequest(url: string): boolean {
  return url.includes('/payment/stripe/connect/');
}

/**
 * Guest booking calls. A 401 here means the payment route rejected the
 * request; it must not clear the panel session.
 */
function isPublicGuestPaymentRequest(url: string): boolean {
  return (
    url.includes('/payment/stripe/setup-intent') ||
    url.includes('/payment/validate') ||
    url.includes('/payment/store')
  );
}

/** Public guest forms must stay usable when the portal session has expired. */
function isPublicGuestFormPath(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return /^\/(?:prmfrm|fidelityfrm|carddetails)(?:[/?#]|$)/.test(
    window.location.pathname,
  );
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: env.API_URL,
  prepareHeaders: (headers, { getState, arg }) => {
    const state = getState() as RootState;
    const url =
      typeof arg === 'string'
        ? arg
        : ((arg as FetchArgs | undefined)?.url ?? '');

    if (isBackofficeLoginRequest(url)) {
      // Login must not send a stale Authorization header.
    } else if (isStripeConnectRequest(url)) {
      // The payment service validates the panel realm, so that token wins here.
      const token =
        state.auth.accessToken ?? state.backofficeAuth.accessToken ?? '';
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    } else if (isSharedMessageDeliveryRequest(url)) {
      const backofficeToken = state.backofficeAuth.accessToken;
      const accessToken = state.auth.accessToken;
      if (backofficeToken) {
        headers.set('Authorization', `Bearer ${backofficeToken}`);
      } else if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }
    } else if (isBackofficeApiRequest(url)) {
      const backofficeToken = state.backofficeAuth.accessToken;
      if (backofficeToken) {
        headers.set('Authorization', `Bearer ${backofficeToken}`);
      }
    } else {
      const accessToken = state.auth.accessToken;
      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }
    }

    // Let the browser set multipart boundary for FormData uploads.
    if (!isFormDataRequest(arg)) {
      headers.set('Content-Type', 'application/json');
    }

    headers.set('Accept', 'application/json');
    return headers;
  },
});

function getSentBearerToken(
  result: Awaited<ReturnType<typeof rawBaseQuery>>,
): string | null {
  const header = result.meta?.request.headers.get('Authorization');
  return header?.startsWith('Bearer ') ? header.slice('Bearer '.length) : null;
}

function getRequestBody(args: string | FetchArgs): unknown {
  if (typeof args === 'string') {
    return undefined;
  }

  return args.body;
}

function getRequestMethod(args: string | FetchArgs): string {
  if (typeof args === 'string') {
    return 'GET';
  }

  return args.method ?? 'GET';
}

function shouldSkipRetry(args: string | FetchArgs): boolean {
  if (getRequestBody(args) instanceof FormData) {
    return true;
  }

  // Only safe, idempotent reads are retried on 5xx.
  return getRequestMethod(args).toUpperCase() !== 'GET';
}

/**
 * Wraps fetchBaseQuery with retry on 5xx for GET only, and logout on 401.
 * PRO20 uses long-lived JWT tokens — no refresh endpoint.
 */
export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);
  let retries = 0;

  const skipRetry = shouldSkipRetry(args);

  while (
    !skipRetry &&
    result.error &&
    typeof result.error.status === 'number' &&
    result.error.status >= 500 &&
    retries < MAX_RETRIES
  ) {
    retries += 1;
    await new Promise((resolve) => setTimeout(resolve, 1000 * retries));
    result = await rawBaseQuery(args, api, extraOptions);
  }

  if (result.error?.status === 401) {
    const url = getRequestUrl(args);
    const state = api.getState() as RootState;
    // Only a 401 for the token currently in the store means that session expired.
    // A stale request (sent before login or with a previous token) must not log out.
    const isCurrentPanelSession =
      Boolean(state.auth.accessToken) &&
      getSentBearerToken(result) === state.auth.accessToken;
    if (isBackofficeLoginRequest(url)) {
      // Invalid credentials — keep current sessions intact.
    } else if (
      isStripeConnectRequest(url) ||
      isPublicGuestPaymentRequest(url)
    ) {
      // A rejected per-branch Stripe call says nothing about the admin session.
    } else if (isSharedMessageDeliveryRequest(url)) {
      if (state.backofficeAuth.accessToken) {
        api.dispatch(clearBackofficeSession());
      } else if (isCurrentPanelSession) {
        api.dispatch(logout());
      }
    } else if (isBackofficeApiRequest(url)) {
      api.dispatch(clearBackofficeSession());
    } else if (isPublicGuestFormPath()) {
      // A 401 on a public guest form must not clear the admin session.
    } else if (isCurrentPanelSession) {
      api.dispatch(logout());
    }
  }

  return result;
};
